import type { ApolloError } from '@apollo/client';
import * as React from 'react';
import classNames from 'classnames';
import {
  Button,
  Heading,
  PieChart,
  Surface,
  Text,
} from '@quality24/design-system';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';

import { DateFilter, PieChartComponentData } from '../../../../@types';

import { formatChartData } from './utils';
import { createReferenceMap } from '../../utils/reference';
import { mapData } from '../../utils/mapper';
import { filterData } from '../../utils/filter';

import styles from '../../DataViewContainer.module.scss';
import { parseDataValue } from '../../../../utils';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, unknown>;
  dateFilter: DateFilter;
  renderOnSurface?: boolean;
  config: Omit<PieChartComponentData, 'type'>;
}

/**
 * <PieChartContainer> page
 */
export const PieChartContainer: React.FunctionComponent<Props> = ({
  className,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  renderOnSurface = true,
  config: {
    title,
    className: configClassName,
    rootKey,
    options,
    displayFirst,
    skipSort,
    groupOthers,
    postQuery,
    references,
    aggrKey,
    groupBy,
    displayLegend,
    surfaceClassName,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as Array<Record<string, string | number>>,
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  /**
   * Post-processing - Calculate percentages
   */
  const chartData = React.useMemo(
    () => formatChartData(mappedData, displayFirst, groupOthers, skipSort),
    [mappedData, displayFirst, skipSort, groupOthers],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('PieChartContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      chartData,
    });
  }

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, surfaceClassName, {
          'on-surface': renderOnSurface,
        })}
        padding="sm"
        backgroundColor="grayLight2"
      >
        {title && (
          <Heading as="h3" size="sm1" weight="bold" className="mb-4">
            {!loading && parseDataValue(data, title, 'string')}
          </Heading>
        )}

        {!loading && !error && aggrData.length === 0 && (
          <Text size="sm2" color="gray100">
            Não há dados disponíveis.
          </Text>
        )}

        {!loading && error && (
          <div className={styles['error-container']}>
            <Text className="mb-2" size="sm2" color="gray100">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {!error && (
          <PieChart
            loading={loading}
            data={chartData}
            options={options}
            height={options?.height}
            displayLegend={displayLegend !== false}
          />
        )}
      </Surface>
    </div>
  );
};

export default PieChartContainer;
